import omit from "lodash-es/omit";
import React, { createRef, useCallback } from "react";
import { v4 as uuid } from "uuid";

import FacetsFilter from "@/mk2/apps/bazaar/components/FacetsFilter";
import Grid, { GridPlaceholder } from "@/mk2/apps/bazaar/components/Grid";
import { GridPageLogic } from "@/mk2/apps/bazaar/components/GridPageLogic";
import SimpleSelect from "@/mk2/apps/bazaar/components/SimpleSelect";
import { SORTING_VALUES } from "@/mk2/apps/bazaar/components/SortLabel";
import TextSearch from "@/mk2/apps/bazaar/components/TextSearch";
import { ITEMS_PER_PAGE } from "@/mk2/apps/bazaar/constants";
import { Props as PageProps } from "@/mk2/apps/bazaar/containers/HerBazaar/HerBazaarPage";
import HerBazaarHeader from "@/mk2/apps/bazaar/containers/HerBazaarHeader/HerBazaarHeader";
import { hashFilter } from "@/mk2/apps/bazaar/helpers/filter";
import useGrid, { GridProps } from "@/mk2/apps/bazaar/hooks/useGrid";
import { ProductEntity } from "@/mk2/apps/bazaar/schemas";
import { bazaarUrl } from "@/mk2/apps/bazaar/urls";
import { Interpolate } from "@/mk2/components/Interpolate";
import { LineHeader, Right } from "@/mk2/components/LineHeader";
import { Pager } from "@/mk2/components/Pager";
import { Panel } from "@/mk2/components/Panel";
import { Failure } from "@/mk2/components/errors/Failure";
import { AdSlot } from "@/mk2/containers/AdSlot/AdSlot";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { cacheLast } from "@/mk2/helpers/cache";
import { LoadingState, isLoading, isSuccess } from "@/mk2/reducers/loadingState";
import { interpolate } from "@/mk2/services/i18n";
import {
    BACK_TO_BAZAAR_GRID,
    BZ2_ONLY_NOT_MARKED,
    BZ2_ONLY_REPORTED,
    BZ2_counter_PRODUCTS,
    PROFILE_username_BLOCKED_BY_USER,
} from "@/mk/autogenerated/translation";
import { formatThousands } from "@/mk/bazaar/common/formatters";
import { canManageBazaar, canReportProducts } from "@/mk/bazaar/common/userUtils";

import styles from "./HerBazaar.mscss";

interface OwnProps {
    onLoad();
}

type Props = OwnProps & Omit<PageProps, "onLoad"> & GridProps;

class HerBazaar extends React.Component<Props> {
    private productsRef = createRef<HTMLDivElement>();

    private cacheSelectedFacets = cacheLast();
    private cachePrice = cacheLast();
    private cacheSortingValues = cacheLast(true);

    // Used as a detection when we should trigger view event
    private lastFilterHash: string;
    private viewUuid: string;

    public componentDidMount() {
        this.trackView();
    }

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if (prevProps.filter.page !== this.props.filter.page) {
            this.productsRef.current.scrollIntoView(true);
        }

        // Visible products has changed - track view
        if (prevProps.products !== this.props.products) {
            this.trackView();
        }
    }

    public render() {
        const {
            seller,
            match,
            loadingState,
            routeNavigate,
            isMobile,
            requestUserPermissions,
            location,
            filter,
            facets,
            hits,
            products,
            onSearchChange,
            onFacetClear,
            onFacetChange,
            onCategoryClear,
            onCategoryChange,
            onPriceClear,
            onPriceChange,
            onComplaintsChange,
            onNotMarkedChange,
            onGridSortChange,
            generateCategoryTo,
            pagerTo,
            onFacetToggle,
            onLoad,
            onPlacesRadiusChange,
            errorMessage,
        } = this.props;

        const isModerator = canReportProducts(requestUserPermissions);
        const sortingValues: Partial<typeof SORTING_VALUES> = this.cacheSortingValues(
            () => {
                let vals: Partial<typeof SORTING_VALUES> = filter.q
                    ? omit(SORTING_VALUES, "watchdog")
                    : omit(SORTING_VALUES, "score_new", "watchdog");
                vals = isModerator
                    ? vals
                    : omit(
                          vals,
                          "complaints",
                          "complaints_asc",
                          "complaint_time",
                          "complaint_time_asc",
                      );

                return vals;
            },
            filter.q,
            isModerator,
        );

        const maxPage = hits ? Math.ceil(hits / ITEMS_PER_PAGE) : 1;

        if (
            seller?.hasBlockedBazaar &&
            seller?.user?.isBlockedSelf &&
            !canManageBazaar(requestUserPermissions)
        ) {
            return (
                <div className={styles.HerBazaar}>
                    <div className={styles.HerBazaar__selfBlocked}>
                        <Interpolate
                            i18nKey={PROFILE_username_BLOCKED_BY_USER}
                            username={<strong>{seller.user.username}</strong>}
                        />
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.HerBazaar}>
                {seller && <HerBazaarHeader seller={seller} match={match} location={location} />}

                {!errorMessage && (
                    <div className={styles.HerBazaar__filtering}>
                        <TextSearch value={filter.q} onChange={onSearchChange} />
                        {facets && (
                            <FacetsFilter
                                isMobile={isMobile}
                                facets={facets}
                                category={filter.cats[0]}
                                selectedFacets={this.cacheSelectedFacets(
                                    {
                                        locations: filter.locations,
                                        ...filter.props,
                                    },
                                    filter.locations,
                                    filter.props,
                                )}
                                price={this.cachePrice(
                                    {
                                        min: filter.price_lo,
                                        max: filter.price_hi,
                                    },
                                    filter.price_lo,
                                    filter.price_hi,
                                )}
                                placesRadius={filter.settings.placesRadius}
                                onFacetClear={onFacetClear}
                                onFacetChange={onFacetChange}
                                onCategoryClear={onCategoryClear}
                                onCategoryChange={onCategoryChange}
                                onFacetToggle={onFacetToggle}
                                onPriceClear={onPriceClear}
                                onPriceChange={onPriceChange}
                                generateCategoryTo={generateCategoryTo}
                                onPlacesRadiusChange={onPlacesRadiusChange}
                            />
                        )}
                    </div>
                )}

                <div className={styles.HerBazaar__content}>
                    {seller && hits !== null && (
                        <LineHeader
                            heading={interpolate(BZ2_counter_PRODUCTS, {
                                counter: formatThousands(hits),
                                count: hits,
                            })}
                        >
                            <Right>
                                <SimpleSelect
                                    className={styles.HerBazaar__gridSort}
                                    selected={filter.settings.gridSort}
                                    options={sortingValues}
                                    type="bigger"
                                    onChange={onGridSortChange}
                                />
                            </Right>
                        </LineHeader>
                    )}

                    {isModerator && !errorMessage && (
                        <div className={styles.HerBazaar__moderatorFilters}>
                            <label className={styles.HerBazaar__moderatorFilters__filter}>
                                <input
                                    type="checkbox"
                                    checked={Boolean(filter.notMarked)}
                                    onChange={onNotMarkedChange}
                                />
                                &nbsp;
                                {BZ2_ONLY_NOT_MARKED}
                            </label>
                            <label className={styles.HerBazaar__moderatorFilters__filter}>
                                <input
                                    type="checkbox"
                                    checked={Boolean(filter.complaints)}
                                    onChange={onComplaintsChange}
                                />
                                &nbsp;
                                {BZ2_ONLY_REPORTED}
                            </label>
                        </div>
                    )}

                    {isMobile && (
                        <AdSlot
                            zone="Market Section - Top 01 - Mobile"
                            location={location}
                            marginBottomWithAd={10}
                        />
                    )}

                    <div ref={this.productsRef} className={styles.HerBazaar__products}>
                        <LoadingSwitch
                            loadingState={loadingState}
                            onLoad={onLoad}
                            onRenderSuccess={this.renderSuccess}
                            onRenderInit={this.renderInit}
                            onRenderFailure={this.renderFailure}
                            hasData={products && products.length > 0}
                        />
                    </div>

                    {maxPage > 1 && (
                        <Panel>
                            <Pager
                                className={styles.HerBazaar__pager}
                                location={location}
                                routeNavigate={routeNavigate}
                                numberOfPages={maxPage}
                                isMobile={isMobile}
                                position="bottom"
                                page={filter.page}
                                to={pagerTo}
                            />
                        </Panel>
                    )}

                    {hits > 0 && !isMobile && (
                        <AdSlot
                            zone="Market Section - Mid 02 - Desktop"
                            location={location}
                            marginTopWithAd={20}
                        />
                    )}
                </div>
            </div>
        );
    }

    private renderInit = () => {
        const { loadingState, filter, hits, products } = this.props;

        if (loadingState === LoadingState.INIT && products && products.length) {
            return this.renderSuccess();
        }

        const maxPage = hits ? Math.ceil(hits / ITEMS_PER_PAGE) : 1;
        // Products count calculated from hits so that we can display correct number of placeholders
        const productsCount =
            filter.page < maxPage ? ITEMS_PER_PAGE : hits % ITEMS_PER_PAGE || ITEMS_PER_PAGE;

        return <GridPlaceholder productsCount={productsCount} />;
    };

    private renderFailure = () => {
        const { errorMessage, onLoad } = this.props;

        return (
            <Failure
                errorMessage={errorMessage}
                onRetry={onLoad}
                backButtonUrl={bazaarUrl}
                backButtonLabel={BACK_TO_BAZAAR_GRID}
            />
        );
    };

    private renderSuccess = () => {
        const {
            filter,
            products,
            checked,
            complaintsPerProduct,
            requestUser,
            requestUserPermissions,
            isMobile,
            loadingState,
            location,
        } = this.props;

        const isModerator = canReportProducts(requestUserPermissions);

        return (
            <Grid
                requestUser={requestUser}
                products={products}
                isModerator={isModerator}
                showCreatedTime={filter.settings.gridSort === "creation"}
                checked={checked}
                complaintsPerProduct={complaintsPerProduct}
                isMobile={isMobile}
                location={location}
                isLoading={isLoading(loadingState)}
                onItemClick={this.trackClick}
            />
        );
    };

    private trackView = () => {
        const { loadingState, products, filter, onStoreEvent } = this.props;

        // Track only if we have successfully loaded data
        if (!isSuccess(loadingState)) {
            return;
        }

        const currentFilterHash = hashFilter(filter);
        if (this.lastFilterHash !== currentFilterHash) {
            this.viewUuid = uuid();
            this.lastFilterHash = currentFilterHash;

            const start = filter.page ? (filter.page - 1) * ITEMS_PER_PAGE : filter.start;

            // generates sentry errors
            // onStoreEvent('bazaar_grid_view', {
            //     view_id: this.viewUuid,
            //     products: JSON.stringify(products.map((p) => p.id)),
            //     filter: JSON.stringify(filter),
            //     filter_category: filter.cats.length > 0 ? filter.cats[0] : null,
            //     filter_locations: JSON.stringify(filter.locations),
            //     filter_places: JSON.stringify(filter.places),
            //     filter_price_max: filter.price_hi,
            //     filter_price_min: filter.price_lo,
            //     filter_query: filter.q,
            //     filter_start: start,
            //     filter_users: JSON.stringify(filter.users),
            //     filter_sellers: filter.sellers,
            //     filter_complaints: filter.complaints,
            //     filter_not_marked: filter.notMarked,
            //     filter_clean_sort: filter.settings.cleanSort,
            //     filter_grid_sort: filter.settings.gridSort,
            //     filter_quality: JSON.stringify(filter.settings.quality),
            // });
        }
    };

    private trackClick = (product: ProductEntity) => {
        // const { onStoreEvent } = this.props;
        //
        // if (this.viewUuid) {
        //     onStoreEvent('bazaar_grid_item_click', {
        //         view_id: this.viewUuid,
        //         product: product.id,
        //     });
        // }
    };
}

const HerBazaarWithLogic: React.FunctionComponent<PageProps> = (props) => {
    const {
        onLoad,
        routeParams: { username },
        location,
    } = props;
    const gridProps = useGrid(props);

    const onLoadCb = useCallback(() => {
        onLoad(username, gridProps.filter, location);
    }, [onLoad, username, gridProps.filter, location]);

    return (
        <GridPageLogic
            className={styles.HerBazaar}
            filter={gridProps.filter}
            facets={gridProps.facets}
            loadingState={gridProps.loadingState}
            errorMessage={gridProps.errorMessage}
            isMobile={props.isMobile}
            location={props.location}
            scroll={props.scroll}
            onLoad={onLoadCb}
            openedFacet={gridProps.openedFacet}
            onFacetChange={gridProps.onFacetChange}
            onPriceChange={gridProps.onPriceChange}
            onPlacesRadiusChange={gridProps.onPlacesRadiusChange}
            generateCategoryTo={gridProps.generateCategoryTo}
            onNavigationActivate={props.onNavigationActivate}
            onNavigationStoreBack={props.onNavigationStoreBack}
        >
            <HerBazaar
                {...props}
                {...gridProps}
                loadingState={props.loadingState}
                onLoad={onLoadCb}
            />
        </GridPageLogic>
    );
};

HerBazaarWithLogic.displayName = "HerBazaarWithLogic";

export default HerBazaarWithLogic;
